import { ICartEntry, MinimalCartEntryType } from 'features/cart/types';

import { LoyaltyInRestaurantIncentiveType as ICartEntryType } from 'generated/graphql-gateway';
import type { ICart } from 'state/loyalty/in-restaurant-redemption';
import { LoyaltyAppliedOffer } from 'state/loyalty/types';

/**
 * Derives the set of loyalty-engine offer ids currently applied to the cart:
 * the persisted `appliedOffers` plus any discount/incentive minimal-cart
 * entries that reference an offer.
 *
 * Empty ids are filtered out so the result is safe to feed directly into the
 * loyalty-service `ids` batch filter (which UUID-validates and rejects blanks)
 * and never spuriously matches a real `loyaltyEngineId` in an "is applied"
 * membership check.
 */
export function getAppliedOfferIds(
  appliedOffers: readonly LoyaltyAppliedOffer[],
  minimalCartEntries: readonly ICartEntry[]
): Set<string> {
  const ids = new Set<string>();

  for (const offer of appliedOffers) {
    if (offer.id) {
      ids.add(offer.id);
    }
  }

  for (const entry of minimalCartEntries) {
    if (
      (entry.type === MinimalCartEntryType.discount ||
        entry.type === MinimalCartEntryType.incentive) &&
      entry.referenceId
    ) {
      ids.add(entry.referenceId);
    }
  }

  return ids;
}

export interface AppliedOfferEvictionCandidate {
  /** `loyaltyEngineId` — batch membership key + minimal-cart `referenceId` match. */
  id: string;
  /**
   * Legacy cart entry / Redux applied-offer key, present only when the offer has
   * a persisted `appliedOffers` record. Absent for a minimal-cart-only offer
   * (e.g. a favorited offer wrapper added via `useAddMappedEntryToCart` without
   * an `applyOffer` dispatch — reorder/recent decompose offers into their child
   * items and never take this path), so the eviction sink can clear its
   * minimal-cart row without a legacy `cartId`.
   */
  cartId?: string;
}

/**
 * Derives the eviction candidates for applied loyalty-service offers from the
 * SAME cart sources the applied-offers batch requests ids from
 * (`getAppliedOfferIds`): persisted `appliedOffers` plus discount/incentive
 * minimal-cart entries. Deduped by `loyaltyEngineId` so an offer present in both
 * representations (e.g. a discount offer applied via `applyOffer` that also has a
 * minimal-cart row) is evicted once.
 *
 * Keeping this symmetric with `getAppliedOfferIds` is the point: the batch asks
 * the backend about a minimal-cart-only offer's id, so its absence from the
 * settled response must be able to evict it. Iterating `appliedOffers` alone
 * leaves a backend-delisted offer that lives only in `minimalCartEntries`
 * visible and priced.
 *
 * `cartId` is sourced only from `appliedOffers`; a minimal-cart-only offer yields
 * `{ id }` with no `cartId`. In-restaurant offers are intentionally excluded —
 * they live in `inRestaurantRedemptionCart` (a separate store this eviction sink
 * cannot clear), unlike `getAppliedOfferIds` which also feeds the batch.
 */
export function getAppliedOfferEvictionCandidates(
  appliedOffers: readonly LoyaltyAppliedOffer[],
  minimalCartEntries: readonly ICartEntry[]
): AppliedOfferEvictionCandidate[] {
  const byId = new Map<string, AppliedOfferEvictionCandidate>();

  for (const offer of appliedOffers) {
    if (offer.id) {
      byId.set(offer.id, { id: offer.id, cartId: offer.cartId ?? undefined });
    }
  }

  for (const entry of minimalCartEntries) {
    if (
      (entry.type === MinimalCartEntryType.discount ||
        entry.type === MinimalCartEntryType.incentive) &&
      entry.referenceId &&
      !byId.has(entry.referenceId)
    ) {
      byId.set(entry.referenceId, { id: entry.referenceId });
    }
  }

  return [...byId.values()];
}

/**
 * Finds the minimal-cart entry that represents an applied loyalty-service offer,
 * so an eviction can clear the minimal-cart representation alongside the legacy
 * cart entry / Redux applied-offer record.
 *
 * Matches on `referenceId` (the `loyaltyEngineId`), NOT `cartId`: the discount
 * row carries the `DISCOUNT_OFFER_CART_ID` sentinel and incentive rows carry
 * their own generated `cartId` distinct from the applied offer's — but both
 * populate `referenceId` with the offer's `loyaltyEngineId` (see
 * `buildDiscountOfferCartEntry` and `getAppliedOfferIds`).
 *
 * Returns `undefined` for a blank id so a missing `loyaltyEngineId` never
 * spuriously matches an entry that also lacks one.
 */
export function findAppliedOfferMinimalCartEntry(
  minimalCartEntries: readonly ICartEntry[],
  loyaltyEngineId: string
): ICartEntry | undefined {
  if (!loyaltyEngineId) {
    return undefined;
  }
  return minimalCartEntries.find(
    entry =>
      (entry.type === MinimalCartEntryType.discount ||
        entry.type === MinimalCartEntryType.incentive) &&
      entry.referenceId === loyaltyEngineId
  );
}

/**
 * Derives the loyalty-engine offer ids for offers redeemed in-restaurant. These
 * live only in `inRestaurantRedemptionCart` (never in Redux `appliedOffers` or
 * the minimal-cart entries), so the applied-offers batch must include them
 * explicitly. Without this an in-restaurant-only offer is absent from the
 * settled batch metadata and therefore from the ON-gate cart-metadata seam,
 * unlike the OFF path where the full offer feed already carries it.
 *
 * Empty ids are filtered out for the same reasons as `getAppliedOfferIds`.
 */
export function getInRestaurantOfferIds(inRestaurantCart: ICart): Set<string> {
  const ids = new Set<string>();

  for (const entry of inRestaurantCart) {
    if (entry.type === ICartEntryType.OFFER) {
      const engineId = entry.details.offer.loyaltyEngineId;
      if (engineId) {
        ids.add(engineId);
      }
    }
  }

  return ids;
}
